// Blender's fractal noise family, in TSL.
//
// The last node type the TSL backend did not cover, and the only substantial
// maths port in the set. It is a line-by-line translation of `HELPER_GLSL.noise2`
// in the GLSL compiler this replaced — the same hash, the same gradient, the same
// five fractal loops in the same order — because that compiler was the REFERENCE
// while both existed, and the pixel A/B against it is the only thing that could
// have caught a liberty taken here. It measured 0.0046 mean difference, all of
// it `aos_hash21`'s conditioning (see below). With the reference gone, THIS IS
// NOW THE DEFINITION: change it and every graph authored against it renders
// differently, plausibly and silently.
//
// SUBSTITUTING three's OWN NOISE IS NOT AN OPTION, which is why this was left
// until last. `mx_fractal_noise_float` is MaterialX's fBM over a different
// gradient basis: it is a fractal noise, not THIS one, so a graph authored
// against Blender's would render a different material — plausibly, and silently.
//
// WHY `Fn` AND NOT A `glslFn`/`wgslFn` PAIR, which is what the header of
// `materialGraphTSL.js` proposed. A pair keeps GLSL as the reference at the cost
// of two implementations of one function, and a leaf function is where that cost
// is smallest — but it is still two, and the failure is silent: the WGSL half
// drifts and only the WebGPU arm changes. `Fn` emits BOTH from one description
// (`GLSLNodeBuilder` and `WGSLNodeBuilder` walk the same tree), so the two
// backends cannot disagree with each other. What remains is this file against the
// GLSL STRING, which the pixel A/B measures.
//
// WHERE THE TWO BACKENDS GENUINELY DISAGREE, AND IT IS NOT THIS PORT.
// `aos_hash21` is `fract(p * vec2(123.34, 456.21))`, then `p += dot(p, p+45.32)`,
// then `fract(p.x * p.y)`. At a grid index around 200 the first product is ~24700,
// where an f32 ulp is 0.004 — so `fract` keeps ~250 distinct values — and the
// second stage multiplies two components of ~92, amplifying that 0.004 into ~0.37
// of the final hash. One ulp of difference anywhere upstream (a contraction into
// an fma, a different `floor` lowering) therefore produces a COMPLETELY different
// gradient at that cell. The function is chaotic above coordinates of ~1.
//
// Measured, same noise, `fac` only, nothing else changed but the sample scale
// (mean |difference| in linear light):
//   diag-noise-snoise   (0..5)     0.00001   max   1    0.00% of pixels over 8
//   diag-noise-bigscale (0..200)   0.02887   max 134   16.16% over 8
// A factor of ~2900 for nothing but where the field is sampled, and the shape is
// salt-and-pepper rather than a tint — which is why the harness reports `max` and
// `pctOver8` beside the mean, since a uniform sub-level offset and a handful of
// wildly wrong texels read the same as a mean.
//
// This matters beyond a diagnostic, because `aos_rand_off2` ADDS 100..200: any
// non-zero `distortion`, and the `color` output in every case, are always in that
// regime. So a material using Noise's Color under WebGPU will not match the same
// material under WebGL, and no amount of care in this file changes that.
//
// The fix is to wrap the grid index before hashing, and it is NOT done here: it
// changes what every existing Noise material looks like on BOTH backends, which
// is a visual change to authored content and therefore somebody's decision rather
// than a porting detail. `noise-fractals.graph.json` — the gated fixture — is
// deliberately confined to the well-conditioned regime, and the diag-noise-*
// fixtures measure the rest.
//
// EVERY FUNCTION CARRIES A `setLayout`. Without one, TSL inlines the body at each
// call site; these are recursive-ish (the five fractals all call `snoise2`, and
// the Noise node calls a fractal three times) so inlining multiplies the shader
// out. The layout also pins the argument TYPES, which is what makes the WGSL
// emitter generate a real function rather than infer from the first call.

import * as TSL from 'three/tsl'

const {
  Fn, Loop, Break, If, float, vec2, int, select,
  floor: tslFloor, fract, abs: tslAbs, clamp, max: tslMax, mix, sin, cos, dot,
} = TSL

const hash21 = Fn(([p_in]) => {
  const p = fract(p_in.mul(vec2(123.34, 456.21))).toVar()
  p.addAssign(dot(p, p.add(45.32)))
  return fract(p.x.mul(p.y))
})
hash21.setLayout({ name: 'aos_hash21', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] })

const grad2 = Fn(([i]) => {
  const a = hash21(i).mul(6.2831853).toVar()
  return vec2(cos(a), sin(a))
})
grad2.setLayout({ name: 'aos_grad2', type: 'vec2', inputs: [{ name: 'i', type: 'vec2' }] })

/** Signed Perlin gradient noise, raw ~[-0.7, 0.7] scaled by 1.4 to ~[-1, 1]. */
const snoise2 = Fn(([p]) => {
  const i = tslFloor(p).toVar()
  const f = fract(p).toVar()
  const u = f.mul(f).mul(float(3).sub(f.mul(2))).toVar()
  const a = dot(grad2(i), f)
  const b = dot(grad2(i.add(vec2(1, 0))), f.sub(vec2(1, 0)))
  const c = dot(grad2(i.add(vec2(0, 1))), f.sub(vec2(0, 1)))
  const d = dot(grad2(i.add(vec2(1, 1))), f.sub(vec2(1, 1)))
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y).mul(1.4)
})
snoise2.setLayout({ name: 'aos_snoise2', type: 'float', inputs: [{ name: 'p', type: 'vec2' }] })

/** The seed offsets used for the domain warp and for the Color output's two
 *  extra channels — decorrelated by sampling the same field far away. */
export const randOff2 = Fn(([s]) => vec2(
  hash21(vec2(s, 0)).mul(100).add(100),
  hash21(vec2(s, 1)).mul(100).add(100),
))
randOff2.setLayout({ name: 'aos_rand_off2', type: 'vec2', inputs: [{ name: 's', type: 'float' }] })

// The zero-distortion early-out is kept as real control flow rather than folded
// into the arithmetic. It is mathematically a no-op either way (the added terms
// are multiplied by `distortion`), but the default IS zero, and unconditional
// would pay two extra `snoise2` evaluations on every graph that leaves it alone.
export const noiseDistort2 = Fn(([p, distortion]) => {
  const out = p.toVar()
  If(distortion.notEqual(0), () => {
    out.assign(p.add(vec2(
      snoise2(p.add(randOff2(float(0)))).mul(distortion),
      snoise2(p.add(randOff2(float(1)))).mul(distortion),
    )))
  })
  return out
})
noiseDistort2.setLayout({
  name: 'aos_noise_distort2',
  type: 'vec2',
  inputs: [{ name: 'p', type: 'vec2' }, { name: 'distortion', type: 'float' }],
})

// ONE SIGNATURE FOR ALL FIVE, exactly as the GLSL helpers have, so the Noise node
// dispatches by name and nothing downstream knows which fractal it picked.
// `offset`/`gain` are unused by fBM and Multifractal; `normalize` is honoured by
// fBM alone — both quirks are Blender's, and reproducing them is the point.
const FRACTAL_INPUTS = [
  { name: 'co', type: 'vec2' },
  { name: 'detail', type: 'float' },
  { name: 'roughness', type: 'float' },
  { name: 'lacunarity', type: 'float' },
  { name: 'offset', type: 'float' },
  { name: 'gain', type: 'float' },
  { name: 'normalize', type: 'float' },
]
const layout = (fn, name) => { fn.setLayout({ name, type: 'float', inputs: FRACTAL_INPUTS }); return fn }

// The loops run a FIXED 0..8 with an early `Break` past `int(detail)`, matching
// the GLSL exactly — a dynamic loop bound is legal in both shading languages, but
// the two would then unroll differently and the octave count is what the
// fractional last octave blends against.
const OCTAVES = 9

const fbm2 = layout(Fn(([co, detail_in, roughness_in, lacunarity, offset, gain, normalize]) => {
  const detail = clamp(detail_in, 0, 8).toVar()
  const roughness = tslMax(roughness_in, 0).toVar()
  const n = int(detail).toVar()
  const fscale = float(1).toVar()
  const amp = float(1).toVar()
  const maxamp = float(0).toVar()
  const sum = float(0).toVar()
  Loop({ start: int(0), end: int(OCTAVES), type: 'int' }, ({ i }) => {
    If(i.greaterThan(n), () => { Break() })
    sum.addAssign(snoise2(co.mul(fscale)).mul(amp))
    maxamp.addAssign(amp)
    amp.mulAssign(roughness)
    fscale.mulAssign(lacunarity)
  })
  const rmd = detail.sub(tslFloor(detail)).toVar()
  // The fractional-octave term is evaluated unconditionally and SELECTED, where
  // the GLSL branches. Same value; `select` is how TSL expresses a value-valued
  // conditional, and an `If` here would need a var per arm for no gain.
  const sum2 = sum.add(snoise2(co.mul(fscale)).mul(amp)).toVar()
  const nrmOne = sum.div(maxamp).mul(0.5).add(0.5)
  const nrmTwo = sum2.div(maxamp.add(amp)).mul(0.5).add(0.5)
  const normed = select(rmd.notEqual(0), mix(nrmOne, nrmTwo, rmd), nrmOne)
  const raw = select(rmd.notEqual(0), mix(sum, sum2, rmd), sum)
  return select(normalize.notEqual(0), normed, raw)
}), 'aos_fbm2')

const multiFractal2 = layout(Fn(([co, detail_in, roughness_in, lacunarity]) => {
  const detail = clamp(detail_in, 0, 8).toVar()
  const roughness = tslMax(roughness_in, 0).toVar()
  const n = int(detail).toVar()
  const p = co.toVar()
  const value = float(1).toVar()
  const pwr = float(1).toVar()
  Loop({ start: int(0), end: int(OCTAVES), type: 'int' }, ({ i }) => {
    If(i.greaterThan(n), () => { Break() })
    value.mulAssign(pwr.mul(snoise2(p)).add(1))
    pwr.mulAssign(roughness)
    p.mulAssign(lacunarity)
  })
  const rmd = detail.sub(tslFloor(detail)).toVar()
  return select(rmd.notEqual(0), value.mul(rmd.mul(pwr).mul(snoise2(p)).add(1)), value)
}), 'aos_multi_fractal2')

const heteroTerrain2 = layout(Fn(([co, detail_in, roughness_in, lacunarity, offset]) => {
  const detail = clamp(detail_in, 0, 8).toVar()
  const roughness = tslMax(roughness_in, 0).toVar()
  const n = int(detail).toVar()
  const p = co.toVar()
  const pwr = roughness.toVar()
  const value = offset.add(snoise2(p)).toVar()
  p.mulAssign(lacunarity)
  Loop({ start: int(1), end: int(OCTAVES), type: 'int' }, ({ i }) => {
    If(i.greaterThan(n), () => { Break() })
    value.addAssign(snoise2(p).add(offset).mul(pwr).mul(value))
    pwr.mulAssign(roughness)
    p.mulAssign(lacunarity)
  })
  const rmd = detail.sub(tslFloor(detail)).toVar()
  If(rmd.notEqual(0), () => {
    value.addAssign(rmd.mul(snoise2(p).add(offset)).mul(pwr).mul(value))
  })
  return value
}), 'aos_hetero_terrain2')

const hybridMultiFractal2 = layout(Fn(([co, detail_in, roughness_in, lacunarity, offset, gain]) => {
  const detail = clamp(detail_in, 0, 8).toVar()
  const roughness = tslMax(roughness_in, 0).toVar()
  const n = int(detail).toVar()
  const p = co.toVar()
  const pwr = float(1).toVar()
  const value = float(0).toVar()
  const weight = float(1).toVar()
  Loop({ start: int(0), end: int(OCTAVES), type: 'int' }, ({ i }) => {
    // Blender's own compound condition: the weight collapsing is what ENDS a
    // hybrid, and it can happen before the octave count does.
    If(weight.lessThanEqual(0.001).or(i.greaterThan(n)), () => { Break() })
    If(weight.greaterThan(1), () => { weight.assign(1) })
    const signal = snoise2(p).add(offset).mul(pwr).toVar()
    pwr.mulAssign(roughness)
    value.addAssign(weight.mul(signal))
    weight.mulAssign(gain.mul(signal))
    p.mulAssign(lacunarity)
  })
  const rmd = detail.sub(tslFloor(detail)).toVar()
  If(rmd.notEqual(0).and(weight.greaterThan(0.001)), () => {
    If(weight.greaterThan(1), () => { weight.assign(1) })
    value.addAssign(rmd.mul(weight).mul(snoise2(p).add(offset)).mul(pwr))
  })
  return value
}), 'aos_hybrid_multi_fractal2')

const ridgedMultiFractal2 = layout(Fn(([co, detail_in, roughness_in, lacunarity, offset, gain]) => {
  const detail = clamp(detail_in, 0, 8).toVar()
  const roughness = tslMax(roughness_in, 0).toVar()
  const n = int(detail).toVar()
  const p = co.toVar()
  const pwr = roughness.toVar()
  const signal = offset.sub(tslAbs(snoise2(p))).toVar()
  signal.mulAssign(signal)
  const value = signal.toVar()
  // No fractional-octave tail here, and that is Blender's, not an omission.
  Loop({ start: int(1), end: int(OCTAVES), type: 'int' }, ({ i }) => {
    If(i.greaterThan(n), () => { Break() })
    p.mulAssign(lacunarity)
    const weight = clamp(signal.mul(gain), 0, 1).toVar()
    signal.assign(offset.sub(tslAbs(snoise2(p))))
    signal.mulAssign(signal)
    signal.mulAssign(weight)
    value.addAssign(signal.mul(pwr))
    pwr.mulAssign(roughness)
  })
  return value
}), 'aos_ridged_multi_fractal2')

/** The `noiseType` enum -> fractal, mirroring NOISE_FRACTAL_FN in
 *  `materialNodes.js`. Keys must stay in step with its options list. */
export const NOISE_FRACTAL_TSL = {
  fbm: fbm2,
  multifractal: multiFractal2,
  hetero: heteroTerrain2,
  ridged: ridgedMultiFractal2,
  hybrid: hybridMultiFractal2,
}
